import {
  peerInfoSchema,
  roomStateSchema,
  shareStateSchema,
} from "@klass-share/protocol";
import type { PeerInfo, RoomState, ShareState } from "./wire.js";

export type RoomEvent =
  | { type: "snapshot"; roomState: RoomState }
  | { type: "peer-joined" | "peer-updated"; peer: PeerInfo }
  | { type: "peer-left"; clientId: string }
  | { type: "share-changed"; share: ShareState }
  | { type: "host-media"; state: "recovering" | "ok" };

export function applyRoomEvent(
  current: RoomState | undefined,
  event: RoomEvent,
): RoomState {
  if (event.type === "snapshot") return cloneRoomState(event.roomState);
  if (current === undefined)
    throw new Error("Room delta arrived before snapshot");
  switch (event.type) {
    case "peer-joined":
    case "peer-updated": {
      const peers = current.peers.filter(
        (peer) => peer.clientId !== event.peer.clientId,
      );
      peers.push(event.peer);
      return { ...current, peers };
    }
    case "peer-left":
      return {
        ...current,
        peers: current.peers.filter((peer) => peer.clientId !== event.clientId),
      };
    case "share-changed":
      return { ...current, share: { ...event.share } };
    case "host-media":
      return { ...current, hostMedia: event.state };
  }
}

export function roomEventFromWire(
  type: string,
  payload: Record<string, unknown>,
): RoomEvent | undefined {
  if (type === "room-state") {
    const parsed = roomStateSchema.safeParse(payload.roomState);
    if (parsed.success) return { type: "snapshot", roomState: parsed.data };
  }
  if (type === "peer-joined" || type === "peer-updated") {
    const parsed = peerInfoSchema.safeParse(payload.peer);
    if (parsed.success) return { type, peer: parsed.data };
  }
  if (type === "peer-left" && typeof payload.clientId === "string") {
    return { type, clientId: payload.clientId };
  }
  if (type === "share-changed") {
    const parsed = shareStateSchema.safeParse(payload.share);
    if (parsed.success) return { type, share: parsed.data };
  }
  if (
    type === "host-media" &&
    (payload.state === "ok" || payload.state === "recovering")
  ) {
    return { type, state: payload.state };
  }
  return undefined;
}

export function shouldReleaseCapture(
  room: RoomState,
  clientId: string,
  hasLocalPendingGrant: boolean,
): boolean {
  return (
    !hasLocalPendingGrant &&
    (room.share.status !== "active" || room.share.sourceClientId !== clientId)
  );
}

export interface CaptureOwnershipReconciliation {
  activationInFlight: boolean;
  releaseCapture: boolean;
}

/**
 * A locally captured grant can become ready before the host's earlier public
 * `pending` delta is delivered. Keep that activation authoritative locally
 * until the room confirms `active:self`; only an authoritative idle (or an
 * explicit denial/revoke handled by the controller) cancels it.
 */
export function reconcileCaptureOwnership(
  room: RoomState,
  clientId: string,
  hasPendingCapture: boolean,
  activationInFlight: boolean,
): CaptureOwnershipReconciliation {
  if (activationInFlight) {
    if (
      room.share.status === "active" &&
      room.share.sourceClientId === clientId
    ) {
      return { activationInFlight: false, releaseCapture: false };
    }
    if (room.share.status === "idle") {
      return { activationInFlight: false, releaseCapture: true };
    }
    return { activationInFlight: true, releaseCapture: false };
  }
  return {
    activationInFlight: false,
    releaseCapture: shouldReleaseCapture(room, clientId, hasPendingCapture),
  };
}

function cloneRoomState(roomState: RoomState): RoomState {
  return {
    ...roomState,
    peers: roomState.peers.map((peer) => ({
      ...peer,
      capabilities: { ...peer.capabilities },
    })),
    share: { ...roomState.share },
  };
}
